import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Button, Row, Col, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import './ReturnDetail.css';

const ReturnDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [returnData, setReturnData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReturnDetail();
    }
  }, [isAuthenticated, id]);

  const fetchReturnDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/returns/my-returns/${id}`);
      setReturnData(response);
    } catch (error) {
      console.error('Error fetching return detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!window.confirm('Are you sure you want to cancel this return request? This action cannot be undone.')) {
      return;
    }

    try {
      await api.put(`/returns/my-returns/${id}/cancel`);
      toast.success('Return request cancelled successfully');
      // Refresh return data to show updated status
      await fetchReturnDetail();
    } catch (error) {
      console.error('Error cancelling return request:', error);
      toast.error(error.response?.data?.error || 'Failed to cancel return request');
    }
  };

  // Generate return number from ID
  const getReturnNumber = (id, createdAt) => {
    const year = new Date(createdAt).getFullYear();
    const paddedId = String(id).padStart(3, '0');
    return `RET-${year}-${paddedId}`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${
      months[date.getMonth()]
    } ${date.getDate()}, ${date.getFullYear()} ${displayHours}:${minutes} ${ampm}`;
  };

  // Helper function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.png';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('/')) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const baseUrl = API_BASE_URL.replace(/\/api$/, '');
      return `${baseUrl}${imagePath}`;
    }
    return imagePath;
  };

  // Get status stages
  const getStatusStages = () => {
    const stages = [
      { key: 'requested', label: 'Requested', date: returnData?.created_at },
      { key: 'approved', label: 'Approved', date: null },
      { key: 'refund_initialized', label: 'Refund Initialized', date: null },
      { key: 'refunded', label: 'Refunded', date: null },
    ];

    // Find dates from tracking history
    if (returnData?.tracking) {
      returnData.tracking.forEach((track) => {
        if (track.status === 'approved') {
          const stage = stages.find((s) => s.key === 'approved');
          if (stage && !stage.date) stage.date = track.created_at;
        } else if (track.status === 'refund_initialized') {
          const stage = stages.find((s) => s.key === 'refund_initialized');
          if (stage && !stage.date) stage.date = track.created_at;
        } else if (track.status === 'refunded') {
          const stage = stages.find((s) => s.key === 'refunded');
          if (stage && !stage.date) stage.date = track.created_at;
        }
      });
    }

    // Also check return status directly and updated_at
    const currentStatus = returnData?.status;
    if (currentStatus === 'approved' && !stages.find((s) => s.key === 'approved')?.date) {
      const stage = stages.find((s) => s.key === 'approved');
      if (stage) stage.date = returnData?.updated_at;
    }
    if (
      currentStatus === 'refund_initialized' &&
      !stages.find((s) => s.key === 'refund_initialized')?.date
    ) {
      const stage = stages.find((s) => s.key === 'refund_initialized');
      if (stage) stage.date = returnData?.updated_at;
    }
    if (currentStatus === 'refunded' && !stages.find((s) => s.key === 'refunded')?.date) {
      const stage = stages.find((s) => s.key === 'refunded');
      if (stage) stage.date = returnData?.updated_at;
    }

    return stages;
  };

  // Get current status index
  const getCurrentStatusIndex = () => {
    const status = returnData?.status;
    const statusMap = {
      pending: 0,
      requested: 0,
      approved: 1,
      refund_initialized: 2,
      refunded: 3,
      return_cancelled: -1, // Special case for cancelled
    };
    return statusMap[status] || 0;
  };

  // Check if return is cancelled
  const isCancelled = returnData?.status === 'return_cancelled';

  if (loading) return null;

  if (!returnData) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <h3>Return request not found</h3>
          <Link to="/returns">
            <Button variant="primary">Back to Returns</Button>
          </Link>
        </div>
      </Container>
    );
  }

  const statusStages = getStatusStages();
  const currentStatusIndex = getCurrentStatusIndex();

  return (
    <div className="return-detail-page">
      <Container>
        {/* Breadcrumbs */}
        <nav className="breadcrumb-nav" aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/returns">Return</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Track Return
            </li>
          </ol>
        </nav>

        <div className="return-header">
          <h1 className="return-detail-title">Return - Product Details</h1>
          <div className="return-meta">
            <div className="return-number-large">
              Return {getReturnNumber(returnData.id, returnData.created_at)}
            </div>
            <div className="return-date">Request Date: {formatDate(returnData.created_at)}</div>
          </div>
        </div>

        {/* Status Progress Bar */}
        {isCancelled ? (
          <div className="status-progress-section cancelled-status">
            <div className="cancelled-message">
              <div className="cancelled-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#dc3545" stroke="#dc3545" strokeWidth="2" />
                  <path
                    d="M15 9l-6 6M9 9l6 6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="cancelled-title">Return Cancelled</h3>
              <p className="cancelled-text">This return request has been cancelled.</p>
              {returnData.admin_comment && (
                <p className="cancelled-reason">Reason: {returnData.admin_comment}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="status-progress-section">
            <div className="status-progress-bar">
              {statusStages.map((stage, index) => {
                const isCompleted = index <= currentStatusIndex;
                const isCurrent = index === currentStatusIndex;

                return (
                  <React.Fragment key={stage.key}>
                    <div
                      className={`status-stage ${isCompleted ? 'completed' : ''} ${
                        isCurrent ? 'current' : ''
                      }`}
                    >
                      <div className={`status-icon ${isCompleted ? 'completed' : ''}`}>
                        {isCompleted ? (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                          >
                            <path
                              d="M20 6L9 17l-5-5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <div className="status-icon-placeholder"></div>
                        )}
                      </div>
                      <div className='flex flex-column align-items-center'>
                        <div className="status-label">{stage.label}</div>
                      {stage.date && (
                        <div className="status-date">{formatDateTime(stage.date)}</div>
                      )}
                      </div>
                    </div>
                    {index < statusStages.length - 1 && (
                      <div
                        className={`status-line ${index < currentStatusIndex ? 'completed' : ''}`}
                      ></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        <Row className="mt-4">
          {/* Left Column */}
          <Col md={8}>
            {/* Items To Return */}
            <Card className="return-section-card">
              <Card.Header className="section-header">Items To Return</Card.Header>
              <Card.Body>
                {(() => {
                  const isCombo = returnData.item_type === 'combo' || !!returnData.combo_id;
                  const comboDetails = returnData.combo_selections_detail || [];
                  const qty = returnData.order_item_quantity || 1;

                  if (isCombo) {
                    return (
                      <div className="return-item return-item-combo">
                        <div className="return-combo-top">
                          <div className="return-item-image">
                            <img
                              src={getImageUrl(returnData.product_image)}
                              alt={returnData.product_name}
                              onError={(e) => {
                                e.target.src = '/placeholder.png';
                              }}
                            />
                            <span className="return-combo-badge">COMBO</span>
                          </div>
                          <div className="return-item-details">
                            <div className="return-item-name">{returnData.product_name}</div>
                            <div className="return-item-specs">
                              {returnData.combo_includes_label ||
                                comboDetails.map((d) => d.product_name).filter(Boolean).join(' + ') ||
                                'Combo'}
                              {' · '}
                              {qty} {qty === 1 ? 'Combo' : 'Combos'}
                            </div>
                            <div className="return-item-price">
                              ₹{parseFloat(returnData.amount_refund || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        {comboDetails.length > 0 && (
                          <div className="return-combo-includes">
                            <div className="return-combo-includes-title">
                              This combo includes ({comboDetails.length} Item
                              {comboDetails.length === 1 ? '' : 's'})
                            </div>
                            <div className="return-combo-includes-grid">
                              {comboDetails.map((d, idx) => (
                                <div key={d.variant_id || idx} className="return-combo-include-card">
                                  <div className="return-combo-include-image">
                                    {d.product_image ? (
                                      <img src={getImageUrl(d.product_image)} alt={d.product_name || ''} />
                                    ) : null}
                                  </div>
                                  <div className="return-combo-include-info">
                                    <div className="return-combo-include-name">{d.product_name}</div>
                                    <div className="return-combo-include-variant">
                                      {[d.color_name, d.size].filter(Boolean).join(' • ') || '—'}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div className="return-item">
                      <div className="return-item-image">
                        <img
                          src={getImageUrl(returnData.product_image)}
                          alt={returnData.product_name}
                          onError={(e) => {
                            e.target.src = '/placeholder.png';
                          }}
                        />
                      </div>
                      <div className="return-item-details">
                        <div className="return-item-name">{returnData.product_name}</div>
                        <div className="return-item-specs">
                          ({returnData.size || 'N/A'}, {returnData.color_name || 'N/A'}, {qty}{' '}
                          {qty === 1 ? 'Item' : 'Items'})
                        </div>
                        <div className="return-item-price">
                          ₹{parseFloat(returnData.amount_refund || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </Card.Body>
            </Card>

            {/* Return Reason */}
            <Card className="return-section-card mt-3">
              <Card.Header className="section-header">Return Reason</Card.Header>
              <Card.Body>
                <div className="return-reason-section">
                  <div className="reason-label">Reason:</div>
                  <div className="reason-value">{returnData.return_reason || 'N/A'}</div>
                  {returnData.admin_comment && (
                    <>
                      <div className="reason-label mt-3">Admin Notes:</div>
                      <div className="reason-value">{returnData.admin_comment}</div>
                    </>
                  )}
                  {returnData.return_video_url && (
                    <div className="return-video-section mt-3">
                      <div className="reason-label">Return Video:</div>
                      <div className="return-video-wrapper">
                        <video
                          src={returnData.return_video_url}
                          controls
                          className="return-video"
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  )}
                  {returnData.mention_reason && (
                    <div className="return-additional-info mt-3">
                      <div className="reason-label">Mention Reason:</div>
                      <div className="reason-value">{returnData.mention_reason}</div>
                    </div>
                  )}
                  {returnData.item_condition && (
                    <div className="return-additional-info mt-3">
                      <div className="reason-label">Item Condition:</div>
                      <div className="reason-value">
                        {returnData.item_condition === 'unused_with_tags' 
                          ? 'Unused, in original condition with tags'
                          : returnData.item_condition === 'unused_without_tags'
                          ? 'Unused, without original tags'
                          : returnData.item_condition}
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Return Instructions */}
            <Card className="return-section-card mt-3">
              <Card.Header className="section-header">Return Instructions</Card.Header>
              <Card.Body>
                <ol className="return-instructions" type={1}>
                  <li>
                    Refunds, returns, or exchanges are available only for damaged, defective, or
                    incorrect items.
                  </li>
                  <li>Customers must report issues within 24 hours of delivery.</li>
                  <li>Returned items must be unused, unwashed, and in original packaging.</li>
                  <li>
                    Return courier charges for customer-initiated returns must be borne by the
                    customer.
                  </li>
                  <li>
                    Exchanges are only applicable for damaged or incorrect items, not for size or
                    preference changes.
                  </li>
                </ol>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column */}
          <Col md={4}>
            {/* Refund Details */}
            <Card className="return-section-card">
              <Card.Header className="section-header">Refund Details</Card.Header>
              <Card.Body>
                <div className="refund-details">
                  <div className="refund-row">
                    <span className="refund-label">Amount:</span>
                    <span className="refund-value">
                      ₹{parseFloat(returnData.amount_refund || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="refund-row">
                    <span className="refund-label">Method:</span>
                    <span className="refund-value">Original Payment Method</span>
                  </div>
                  <div className="refund-row">
                    <span className="refund-label">Expected:</span>
                    <span className="refund-value">Within 3-5 business days</span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Actions */}
            <Card className="return-section-card mt-3">
              <Card.Header className="section-header">Actions</Card.Header>
              <Card.Body>
                <div className="return-actions">
                  <a
                    href="tel:+919444363482"
                    className="action-btn contact-action-button flex text-decoration-none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    Contact Support
                  </a>
                 

                  {(returnData.status === 'pending' || returnData.status === 'approved') && (
                    <Button 
                      className="action-btn return-action-button flex"
                      onClick={handleCancelRequest}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Cancel Request
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Shipping History */}
        {returnData.tracking && returnData.tracking.length > 0 && (
          <Card className="return-section-card mt-4">
            <Card.Header className="section-header">Shipping History</Card.Header>
            <Card.Body>
              <div className="shipping-history">
                {(returnData.tracking || []).map((track, index, arr) => (
                  <div key={track.id ?? index} className="history-item">
                    <div className="history-icon">
                      <div className="history-dot"></div>
                      {index < arr.length - 1 && (
                        <div className="history-line"></div>
                      )}
                    </div>
                    <div className="history-content">
                      <div className="history-status">
                        {track.status.replace('_', ' ').toUpperCase()}
                      </div>
                      <div className="history-date">{formatDateTime(track.created_at)}</div>
                      {track.notes && <div className="history-notes">{track.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default ReturnDetail;
